/// <reference types="vite/client" />

import { client } from "@sunner-cloud/api-client";

import { withHttpStatus } from "@/utils/withHttpStatus";

/**
 * Construye la URL base de la API para producción
 * basándose en el dominio actual del navegador
 */
const buildApiBaseUrl = (): string => {
  // Verificar que window esté disponible (evita errores en SSR o Node.js)
  if (typeof window === "undefined") {
    throw new Error(
      "window is not available. API URL cannot be constructed in non-browser environments.",
    );
  }

  const { protocol, hostname } = window.location;
  return `${protocol}//api.${hostname}`;
};

/**
 * Determina la URL base para las peticiones API
 * Orden de precedencia:
 * 1. Variable de entorno VITE_API_URL (para testing contra prod)
 * 2. URL construida dinámicamente (en build de producción)
 * 3. localhost:3000 (en desarrollo local)
 */
const resolveApiBaseUrl = (): string => {
  if (import.meta.env.VITE_E2E_API_URL as string | undefined) {
    return String(import.meta.env.VITE_E2E_API_URL);
  }

  // Primero: chequear si se sobrescribe con variable de entorno
  if (import.meta.env.VITE_API_URL) {
    return String(import.meta.env.VITE_API_URL);
  }

  // Segundo: si es build de producción, construir desde el dominio actual
  if (import.meta.env.PROD) {
    return buildApiBaseUrl();
  }

  // Por defecto: backend local
  return "http://localhost:3000";
};

export const baseUrl: string = resolveApiBaseUrl();

// Configurar el cliente API global con la URL base correcta
// y cargar el token JWT si existe en localStorage
const TOKEN_KEY = "sunner_auth_token";
const TOKEN_EXPIRY_KEY = "sunner_auth_token_expiry";

const storedToken = localStorage.getItem(TOKEN_KEY);
const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);

const headers: Record<string, string> = {};

// Si hay un token válido y no ha expirado, configurarlo en el cliente
if (storedToken && expiryTime && Date.now() <= parseInt(expiryTime, 10)) {
  headers.Authorization = `Bearer ${storedToken}`;
}

client.setConfig({ baseUrl, headers });
client.interceptors.error.use(withHttpStatus);
